import { useCallback, useEffect, useState } from 'react';
import { StringParam } from 'use-query-params';

// OPTION TYPES

export interface SearchEmbedOptions {
    /**
     * The initial search parameters to filter on,
     * e.g. `{ accommodationType: 'all' }`
     * @default ''
     */
    searchParams: string | Record<string, string>;
    /**
     * The initial type of view to show
     * @default 'list'
     */
    viewType: 'map' | 'list';
    /**
     * Enable/disable the left side filter bar
     * @default true
     */
    enableFilters: boolean;
    /**
     * Enable/disable the search box that allows you to change dates/travel party
     * @default true
     */
    enableSearch: boolean;
    /**
     * Enable/disable the sorting type buttons
     * @default true
     */
    enableSort: boolean;
    /**
     * Enable/disable the view type buttons (map/list)
     * @default true
     */
    enableViewType: boolean;
    /**
     * Enable/disable the last minute icons by default
     * @default false
     */
    enableLastMinute: boolean;
}

export interface PlannerEmbedOptions {
    /**
     * The slug of the rental unit to show the planner for
     */
    rentalUnitSlug?: string;
    /**
     * The ID of the rental unit to show the planner for
     */
    rentalUnitId?: string;
}

export const plannerParams = {
    rentalUnitSlug: StringParam,
    rentalUnitId: StringParam,
};
// CALL BACK TYPES

export interface SearchEmbedCallbacks {
    /**
     * Triggered when a search result is clicked, can be used to navigate to a different page for example.
     *
     * ```js
     * onHitClick: (unit, searchParams) => {
     *   window.location.href = `/accommodation/${unit.slug}?${searchParams}`;
     * }
     * ```
     */
    onHitClick?: (unit: { slug: string; id: string }, searchParams: string) => void;
}

// export interface PlannerEmbedCallbacks {
//     /**
//      * Triggered when no availability was found for the next 3 months.
//      */
//     onNoAvailability?: () => void;
// }

export interface SearchBoxEmbedCallbacks {
    /**
     * Triggered when the search box is submitted.
     */
    onSubmit?: (searchParams: string) => void;
}

export interface IFramePagePosition {
    enabled: boolean;
    iFrameDistanceFromTop: number;
    parentDimensions: {
        width: number;
        height: number;
    };
}

// EVENT TYPES

interface IframeReadyEvent {
    type: 'iframeReady';
}

interface SetSearchEmbedOptionsEvent {
    type: 'setSearchEmbedOptions';
    options: SearchEmbedOptions;
}
interface SetPlannerEmbedOptionsEvent {
    type: 'setPlannerEmbedOptions';
    options: PlannerEmbedOptions;
}
interface SetIFramePagePositionEvent {
    type: 'setIFramePagePosition';
    options: IFramePagePosition;
}
type TriggerSearchEmbedCallbackEvent = {
    [K in keyof SearchEmbedCallbacks]: {
        type: 'triggerSearchCallback';
        name: K;
        args: Parameters<Required<SearchEmbedCallbacks>[K]>;
    };
}[keyof SearchEmbedCallbacks];
type TriggerSearchBoxEmbedCallbackEvent = {
    [K in keyof SearchBoxEmbedCallbacks]: {
        type: 'triggerSearchBoxCallback';
        name: K;
        args: Parameters<Required<SearchBoxEmbedCallbacks>[K]>;
    };
}[keyof SearchBoxEmbedCallbacks];
interface IframeDimensionsEvent {
    type: 'iframeDimensions';
    dimensions: IframeDimensions;
}
interface IframeFullScreenToggleEvent {
    type: 'iframeFullScreenToggle';
    fullscreen: boolean;
}
export interface IframeDimensions {
    width: number;
    height: number;
    scrollHeight: number;
    scrollWidth: number;
}

interface ChangeViewTypeEvent {
    type: 'changeViewType';
    viewType: 'map' | 'list';
}

export declare type EmbedEvent = { source: 'travelbase' } & (
    | IframeReadyEvent
    | IframeDimensionsEvent
    | IframeFullScreenToggleEvent
    | SetSearchEmbedOptionsEvent
    | SetPlannerEmbedOptionsEvent
    | SetIFramePagePositionEvent
    | TriggerSearchEmbedCallbackEvent
    | TriggerSearchBoxEmbedCallbackEvent
    | ChangeViewTypeEvent
);

// HOOKS

export const useSendEvent = () =>
    useCallback((event: EmbedEvent) => {
        window.parent.postMessage(event, '*');
    }, []);

const useEmbedEvents = () => {
    const [searchEmbedOptions, setSearchEmbedOptions] = useState<SearchEmbedOptions>({
        searchParams: '',
        viewType: 'list',
        enableFilters: true,
        enableSearch: true,
        enableSort: true,
        enableViewType: true,
        enableLastMinute: false,
    });

    const [iFramePagePosition, setIFramePagePosition] = useState<IFramePagePosition>({
        enabled: false,
        iFrameDistanceFromTop: 0,
        parentDimensions: {
            height: 0,
            width: 0,
        },
    });
    const [plannerEmbedOptions, setPlannerEmbedOptions] = useState<PlannerEmbedOptions | null>(null);
    const [viewType, setViewType] = useState<'list' | 'map'>(searchEmbedOptions.viewType || 'list');
    const sendEvent = useSendEvent();
    const onMessage = (e: MessageEvent) => {
        const event = e.data as EmbedEvent;
        if (event.source !== 'travelbase') {
            return;
        }
        switch (event.type) {
            case 'setSearchEmbedOptions': {
                setSearchEmbedOptions(opts => ({ ...opts, ...event.options }));
                break;
            }
            case 'setPlannerEmbedOptions': {
                setPlannerEmbedOptions(event.options);
                break;
            }
            case 'setIFramePagePosition': {
                setIFramePagePosition(event.options);
                break;
            }
            case 'changeViewType': {
                setViewType(event.viewType);
                break;
            }
            default:
                break;
        }
    };
    const triggerSearchCallback = (
        name: keyof SearchEmbedCallbacks,
        ...args: Parameters<Required<SearchEmbedCallbacks>[keyof SearchEmbedCallbacks]>
    ) => {
        sendEvent({
            type: 'triggerSearchCallback',
            name,
            args,
            source: 'travelbase',
        });
    };
    const triggerSearchBoxCallback = (
        name: keyof SearchBoxEmbedCallbacks,
        ...args: Parameters<Required<SearchBoxEmbedCallbacks>[keyof SearchBoxEmbedCallbacks]>
    ) => {
        sendEvent({
            type: 'triggerSearchBoxCallback',
            name,
            args,
            source: 'travelbase',
        });
    };
    useEffect(() => {
        window.addEventListener('message', onMessage);
        sendEvent({ type: 'iframeReady', source: 'travelbase' });
        return () => {
            window.removeEventListener('message', onMessage);
        };
    }, [sendEvent]);
    useEffect(() => {
        const targetNode = document.body;

        const callback = () => {
            const rect = document.body.getBoundingClientRect();
            sendEvent({
                type: 'iframeDimensions',
                dimensions: {
                    width: rect.width,
                    height: rect.height,
                    scrollHeight: document.body.scrollHeight,
                    scrollWidth: document.body.scrollWidth,
                },
                source: 'travelbase',
            });
        };

        const observer = new MutationObserver(callback);

        observer.observe(targetNode, { childList: true, subtree: true, attributes: true });
        return () => {
            observer.disconnect();
        };
    }, [sendEvent]);
    return {
        sendEvent,
        searchEmbedOptions,
        plannerEmbedOptions,
        iFramePagePosition,
        viewType,
        triggerSearchCallback,
        triggerSearchBoxCallback,
    };
};
export default useEmbedEvents;
